import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader,
  Button,
  IconButton,
  Divider,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  ShoppingCart,
  AttachMoney,
  MoreVert,
  Refresh,
  Download,
  CalendarToday,
  KeyboardArrowDown,
  AddCircleOutline,
  BarChart as BarChartIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import GoogleSheetsExport from '../google-sheets/GoogleSheetsExport';
import {
  ShopifyIcon as SiShopify,
  FacebookIcon as SiFacebook,
  GoogleIcon as SiGoogle,
  AmazonIcon as SiAmazon,
  TikTokIcon as SiTiktok,
  SlackIcon as SiSlack,
  PinterestIcon as SiPinterest,
  QuickBooksIcon as SiQuickbooks,
  PayPalIcon as SiPaypal,
  WiseIcon as SiWise,
  RevolutIcon as SiRevolut,
  GoogleSearchConsoleIcon as SiGoogleSearchConsole,
  GoogleAnalyticsIcon as SiGoogleAnalytics,
  GoogleMerchantCenterIcon as SiGoogleMerchantCenter,
  GoogleAdsIcon as SiGoogleAds,
  KlaviyoIcon as SiKlaviyo,
  SMSBumpIcon as SiSMSBump,
  ShipStationIcon as SiShipStation,
  ShipHeroIcon as SiShipHero,
  Cin7Icon as SiCin7,
  AmericanExpressIcon as SiAmex,
  PayoneerIcon as SiPayoneer,
  MicrosoftIcon as SiMicrosoft,
  MiraklIcon as SiMirakl,
  TargetIcon as SiTarget,
  ImpactIcon as SiImpact,
  DorIcon as SiDor,
  LightspeedIcon as SiLightspeed,
  THGIcon as SiTHG,
  SPSCommerceIcon as SiSPSCommerce,
  ProcurosIcon as SiProcuros
} from '../../assets/icons/BrandIcons';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import dataService from '../../services/dataService';
import connectorService from '../../services/connectorService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#9146FF'];

// Data validation helper to prevent corrupted values from being displayed
const sanitizeMetricValue = (value, maxThreshold = 1000000000, fieldName = 'unknown') => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (!numericValue || isNaN(numericValue) || numericValue < 0) {
    return 0;
  }
  
  if (numericValue > maxThreshold) {
    console.warn(`[DATA SANITIZATION] Corrupted value detected in ${fieldName}: ${numericValue.toLocaleString()}. Setting to 0 to prevent dashboard corruption.`);
    return 0;
  }
  
  return numericValue;
};

// Validate and clean chart data
const sanitizeChartData = (chartData, revenueField = 'revenue') => {
  if (!Array.isArray(chartData)) return [];
  
  return chartData.map(item => ({
    ...item,
    [revenueField]: sanitizeMetricValue(item[revenueField], 1000000000, `chart.${revenueField}`),
    orders: Math.max(0, parseInt(item.orders) || 0)
  })).filter(item => item.date); // Remove items without dates
};

// Run an async task over `items` with a bounded number of in-flight tasks,
// preserving input order. Firing every brand's queries at once (~brands x 5
// heavy ClickHouse queries) overwhelmed the DB and caused silent per-brand
// timeouts that under-reported the company total. Bounding concurrency keeps
// each query fast enough to finish, so the aggregate stays complete.
const mapWithConcurrency = async (items, limit, task) => {
  const results = new Array(items.length);
  let next = 0;
  const runWorker = async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await task(items[index], index);
    }
  };
  const workerCount = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: workerCount }, runWorker));
  return results;
};

// Retry an async thunk on failure (transient timeouts under load), with a
// small increasing backoff. Resolves on first success; rethrows the last
// error once attempts are exhausted so the caller can treat it as failed.
const withRetry = async (thunk, attempts = 3, delayMs = 500) => {
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await thunk();
    } catch (err) {
      lastError = err;
      if (attempt < attempts) {
        await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
      }
    }
  }
  throw lastError;
};

// Get connector icon based on type - matches ConnectorSetup.js
const getConnectorIcon = (type, name) => {
  const iconSize = 24;
  const iconStyle = { fontSize: iconSize };

  // Normalize the type/name for consistent matching
  const platformName = (type || name || '').toLowerCase();

  if (platformName.includes('shopify')) {
    return <SiShopify size={iconSize} />;
  }
  if (platformName.includes('facebook') || platformName.includes('meta')) {
    return <SiFacebook size={iconSize} />;
  }
  if (platformName.includes('google_ads') || platformName.includes('google ads')) {
    return <SiGoogleAds size={iconSize} />;
  }
  if (platformName.includes('google_search_console') || platformName.includes('search console')) {
    return <SiGoogleSearchConsole size={iconSize} />;
  }
  if (platformName.includes('google_analytics') || platformName.includes('analytics')) {
    return <SiGoogleAnalytics size={iconSize} />;
  }
  if (platformName.includes('google_merchant_center') || platformName.includes('merchant center')) {
    return <SiGoogleMerchantCenter size={iconSize} />;
  }
  if (platformName.includes('amazon')) {
    return <SiAmazon size={iconSize} />;
  }
  if (platformName.includes('tiktok')) {
    return <SiTiktok size={iconSize} />;
  }
  if (platformName.includes('slack')) {
    return <SiSlack size={iconSize} />;
  }
  if (platformName.includes('klaviyo')) {
    return <SiKlaviyo size={iconSize} />;
  }
  if (platformName.includes('smsbump') || platformName.includes('sms')) {
    return <SiSMSBump size={iconSize} />;
  }
  if (platformName.includes('shipstation')) {
    return <SiShipStation size={iconSize} />;
  }
  if (platformName.includes('shiphero')) {
    return <SiShipHero size={iconSize} />;
  }
  if (platformName.includes('cin7')) {
    return <SiCin7 size={iconSize} />;
  }
  if (platformName.includes('pinterest')) {
    return <SiPinterest size={iconSize} />;
  }
  if (platformName.includes('quickbooks')) {
    return <SiQuickbooks size={iconSize} />;
  }
  if (platformName.includes('microsoft_ads') || platformName.includes('microsoft ads') || platformName.includes('bing')) {
    return <SiMicrosoft size={iconSize} />;
  }
  if (platformName.includes('paypal')) {
    return <SiPaypal size={iconSize} />;
  }
  if (platformName.includes('payoneer')) {
    return <SiPayoneer size={iconSize} />;
  }
  if (platformName.includes('amex') || platformName.includes('american express')) {
    return <SiAmex size={iconSize} />;
  }
  if (platformName.includes('wise') || platformName.includes('transferwise')) {
    return <SiWise size={iconSize} />;
  }
  if (platformName.includes('revolut')) {
    return <SiRevolut size={iconSize} />;
  }
  if (platformName.includes('mirakl')) {
    return <SiMirakl size={iconSize} />;
  }
  if (platformName.includes('target_plus') || platformName.includes('target plus')) {
    return <SiTarget size={iconSize} />;
  }
  if (platformName.includes('impact')) {
    return <SiImpact size={iconSize} />;
  }
  if (platformName === 'dor' || platformName.startsWith('dor_')) {
    return <SiDor size={iconSize} />;
  }
  if (platformName.includes('lightspeed')) {
    return <SiLightspeed size={iconSize} />;
  }
  if (platformName === 'thg' || platformName.includes('thg_procure') || platformName.includes('thg-procure') || platformName.includes('thg procure')) {
    return <SiTHG size={iconSize} />;
  }
  // 'sps' alone would substring-match 'usps' — keep this to the explicit SPS Commerce forms.
  if (platformName.includes('sps_commerce') || platformName.includes('sps-commerce') || platformName.includes('sps commerce')) {
    return <SiSPSCommerce size={iconSize} />;
  }
  if (platformName.includes('procuros')) {
    return <SiProcuros size={iconSize} />;
  }

  // Default icon
  return <AnalyticsIcon sx={iconStyle} />;
};

// Get connector brand color - matches ConnectorSetup.js
const getConnectorColor = (type, name) => {
  // Normalize the type/name for consistent matching
  const platformName = (type || name || '').toLowerCase();
  
  if (platformName.includes('shopify')) {
    return { main: '#7AB55C', light: '#E8F5E1', dark: '#558B2F' }; // Shopify green
  }
  if (platformName.includes('facebook') || platformName.includes('meta')) {
    return { main: '#1877F2', light: '#E3F2FD', dark: '#1565C0' }; // Facebook blue
  }
  if (platformName.includes('google_ads') || platformName.includes('google ads')) {
    return { main: '#EA4335', light: '#FFEBEE', dark: '#C62828' }; // Google red
  }
  if (platformName.includes('amazon')) {
    return { main: '#FF9900', light: '#FFF3E0', dark: '#E65100' }; // Amazon orange
  }
  if (platformName.includes('tiktok')) {
    return { main: '#FE2C55', light: '#FCE4EC', dark: '#C2185B' }; // TikTok pink
  }
  if (platformName.includes('slack')) {
    return { main: '#4A154B', light: '#F3E5F5', dark: '#7B1FA2' }; // Slack purple
  }
  if (platformName.includes('pinterest')) {
    return { main: '#E60023', light: '#FFEBEE', dark: '#C62828' }; // Pinterest red
  }
  if (platformName.includes('google_search_console') || platformName.includes('search console')) {
    return { main: '#4285F4', light: '#E3F2FD', dark: '#1976D2' }; // Google blue
  }
  if (platformName.includes('google_analytics') || platformName.includes('analytics')) {
    return { main: '#FF6F00', light: '#FFF3E0', dark: '#E65100' }; // Google Analytics orange
  }
  if (platformName.includes('quickbooks')) {
    return { main: '#0077C5', light: '#E3F2FD', dark: '#1976D2' }; // QuickBooks blue
  }
  if (platformName.includes('microsoft_ads') || platformName.includes('microsoft ads') || platformName.includes('bing')) {
    return { main: '#00BCF2', light: '#E1F5FE', dark: '#0277BD' }; // Microsoft blue
  }
  if (platformName.includes('klaviyo') || platformName.includes('email')) {
    return { main: '#9C27B0', light: '#F3E5F5', dark: '#7B1FA2' }; // Klaviyo purple
  }
  if (platformName.includes('smsbump') || platformName.includes('sms')) {
    return { main: '#4CAF50', light: '#E8F5E9', dark: '#388E3C' }; // SMS green
  }
  if (platformName.includes('paypal')) {
    return { main: '#003087', light: '#E3F2FD', dark: '#001F5C' }; // PayPal blue
  }
  if (platformName.includes('payoneer')) {
    return { main: '#FF6C37', light: '#FFE8E0', dark: '#D94A1C' }; // Payoneer orange
  }
  if (platformName.includes('wise') || platformName.includes('transferwise')) {
    return { main: '#37517E', light: '#E8EAF0', dark: '#1F2D4A' }; // Wise dark blue
  }
  if (platformName.includes('revolut')) {
    return { main: '#0075EB', light: '#E3F2FD', dark: '#0055B8' }; // Revolut blue
  }
  if (platformName === 'dor' || platformName.startsWith('dor_')) {
    return { main: '#DF1E25', light: '#FDE8E9', dark: '#A81217' }; // Dor red (official badge)
  }
  if (platformName.includes('lightspeed')) {
    return { main: '#ED5153', light: '#FDEAEA', dark: '#C03436' }; // Lightspeed coral
  }
  if (platformName.includes('mirakl')) {
    return { main: '#03182F', light: '#E5E9EE', dark: '#020F1E' }; // Mirakl navy
  }
  if (platformName.includes('target_plus') || platformName.includes('target plus')) {
    return { main: '#CC0000', light: '#FBE5E5', dark: '#8F0000' }; // Target red
  }
  if (platformName.includes('impact')) {
    return { main: '#1B1B1B', light: '#EBEBEB', dark: '#000000' }; // impact.com black
  }

  // Default color
  return { main: '#2196F3', light: '#E3F2FD', dark: '#1976D2' };
};

// Get user-friendly connector display name
const getConnectorDisplayName = (type, name) => {
  // Normalize the type/name for consistent matching
  const platformName = (type || name || '').toLowerCase();
  
  if (platformName.includes('shopify')) {
    return 'Shopify';
  }
  if (platformName.includes('facebook') || platformName.includes('meta')) {
    return 'Meta Ads';
  }
  if (platformName.includes('google_ads') || platformName.includes('google ads')) {
    return 'Google Ads';
  }
  if (platformName.includes('amazon_ads')) {
    return 'Amazon Ads';
  }
  if (platformName.includes('amazon')) {
    return 'Amazon';
  }
  if (platformName.includes('tiktok')) {
    return 'TikTok Ads';
  }
  if (platformName.includes('slack')) {
    return 'Slack';
  }
  if (platformName.includes('pinterest')) {
    return 'Pinterest Ads';
  }
  if (platformName.includes('google_search_console') || platformName.includes('search console')) {
    return 'Google Search Console';
  }
  if (platformName.includes('google_analytics') || platformName.includes('analytics')) {
    return 'Google Analytics 4';
  }
  if (platformName.includes('quickbooks')) {
    return 'QuickBooks';
  }
  if (platformName.includes('microsoft_ads') || platformName.includes('microsoft ads') || platformName.includes('bing')) {
    return 'Microsoft Ads';
  }
  if (platformName.includes('klaviyo')) {
    return 'Klaviyo';
  }
  if (platformName.includes('smsbump') || platformName.includes('sms')) {
    return 'SMS Bump';
  }
  if (platformName.includes('paypal')) {
    return 'PayPal Business';
  }
  if (platformName.includes('payoneer')) {
    return 'Payoneer';
  }
  if (platformName.includes('wise') || platformName.includes('transferwise')) {
    return 'Wise';
  }
  if (platformName.includes('revolut')) {
    return 'Revolut Business';
  }

  // Return original name if no match, but capitalize first letter
  const originalName = name || type || 'Unknown Connector';
  return originalName.charAt(0).toUpperCase() + originalName.slice(1);
};

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState('30days');
  const [selectedBrand, setSelectedBrand] = useState('all'); // 'all' for company view, brand ID for specific brand
  const [anchorEl, setAnchorEl] = useState(null);
  const [brandFilterAnchorEl, setBrandFilterAnchorEl] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasBrands, setHasBrands] = useState(true); // Track if company has any brands
  const [dashboardData, setDashboardData] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [connectors, setConnectors] = useState([]);
  const [metricsData, setMetricsData] = useState({
    revenue: { value: 0, change: 0, trend: 'stable' },
    orders: { value: 0, change: 0, trend: 'stable' },
    customers: { value: 0, change: 0, trend: 'stable' },
    conversion: { value: 0, change: 0, trend: 'stable' }
  });
  const [chartData, setChartData] = useState({
    revenue: [],
    marketing: [],
    products: [],
    activity: []
  });
  const [dataSourcesStatus, setDataSourcesStatus] = useState(null);
  const [brandData, setBrandData] = useState([]);
  const [allBrandData, setAllBrandData] = useState([]); // Store all brand data for filtering
  const [incompleteBrands, setIncompleteBrands] = useState([]); // Brands whose data failed to load (company total is partial)
  const [platformData, setPlatformData] = useState({ platforms: [], totalRevenue: 0, totalOrders: 0, totalCustomers: 0 });
  
  // Custom date range state
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customDateLabel, setCustomDateLabel] = useState('');
  
  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);
  
  useEffect(() => {
    // Fetch connectors when component mounts or brand selection changes
    if (brandData.length > 0) {
      fetchConnectors();
    }
  }, [selectedBrand, brandData]);
  
  useEffect(() => {
    // Update dashboard data when brand filter changes
    if (allBrandData.length > 0) {
      updateDashboardForSelectedBrand();
    }
  }, [selectedBrand, allBrandData]);
  
  useEffect(() => {
    // Fetch platform breakdown when brand or timeRange changes
    if (selectedBrand && brandData.length > 0) {
      fetchPlatformBreakdown();
    }
  }, [selectedBrand, timeRange, brandData]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const companyId = user?.companyId || 1;
      
      // First, fetch all brands for this company
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
      const brandsResponse = await fetch(`${apiUrl}/api/brands`, {
        headers: {
          'x-auth-token': token
        }
      });
      const brands = brandsResponse.ok ? await brandsResponse.json() : [];
      
      // Filter out non-real brands from dashboard
      const hiddenSlugs = ['company-level', 'indicaskincare', 'hydrogenwaterbottles'];
      const actualBrands = brands.filter(b => !hiddenSlugs.includes(b.slug));
      
      if (!actualBrands || actualBrands.length === 0) {
        setHasBrands(false);
        setLoading(false);
        return;
      }
      setHasBrands(true);
      
      console.log(`Fetching data for ${actualBrands.length} brands:`, actualBrands.map(b => `${b.name} (ID: ${b.id})`));
      
      // Fetch each brand's data with bounded concurrency + per-call retry.
      // Previously every brand fired at once (~brands x 5 heavy queries); under
      // that load some calls timed out, were swallowed by allSettled to null,
      // and silently counted as 0 — under-reporting the company total (e.g.
      // showing ~260K instead of the true ~490K). Limiting in-flight work and
      // retrying transient failures keeps the aggregate complete and stable.
      const fetchBrand = async (brand) => {
        const [salesData, marketingData, customerData, sessionData, productData] = await Promise.allSettled([
          withRetry(() => dataService.getSalesData(brand.id, timeRange, 'daily')),
          withRetry(() => dataService.getMarketingData(brand.id, timeRange, 'daily')),
          withRetry(() => dataService.getCustomerData(brand.id, timeRange)),  // Pass timeRange instead of limit
          withRetry(() => dataService.getSessionData(brand.id, timeRange)),
          withRetry(() => dataService.getProductData(brand.id, 100))
        ]);

        return {
          brand,
          salesData: salesData.status === 'fulfilled' ? salesData.value : null,
          marketingData: marketingData.status === 'fulfilled' ? marketingData.value : null,
          customerData: customerData.status === 'fulfilled' ? customerData.value : null,
          sessionData: sessionData.status === 'fulfilled' ? sessionData.value : null,
          productData: productData.status === 'fulfilled' ? productData.value : null
        };
      };

      const allBrandData = await mapWithConcurrency(actualBrands, 4, fetchBrand);

      // If a brand's revenue call still failed after retries, the company total
      // is a partial — record it so the UI can flag it instead of presenting an
      // undercount as the complete figure.
      const failedBrands = allBrandData.filter(b => b.salesData === null).map(b => b.brand.name);
      setIncompleteBrands(failedBrands);
      if (failedBrands.length > 0) {
        console.warn(`[DASHBOARD] Company total is partial — ${failedBrands.length} brand(s) failed to load after retries:`, failedBrands);
      }
      
      // Aggregate company-level metrics
      let totalRevenue = 0;
      let totalOrders = 0;
      let totalNewCustomers = 0;
      let totalReturningCustomers = 0;
      let totalSessions = 0;
      let totalConversions = 0;
      let revenueGrowthRateSum = 0;
      let customerGrowthRateSum = 0;
      let conversionGrowthRateSum = 0;
      let brandsWithRevenueData = 0;
      let brandsWithCustomerData = 0;
      let brandsWithConversionData = 0;
      
      console.log('=== DEBUG: Brand Data Analysis ===');
      const aggregatedRevenueData = new Map();
      const aggregatedMarketingChannels = new Map();
      const aggregatedProducts = new Map();
      
      // Process each brand's data with validation
      allBrandData.forEach(({ brand, salesData, marketingData, customerData, sessionData, productData }) => {
        console.log(`Brand: ${brand.name}`);
        console.log(`  Revenue API Response:`, salesData);
        console.log(`  Customer API Response:`, customerData);
        
        // Aggregate revenue data using totals from backend API (not chartData)
        if (salesData) {
          // Use totalRevenue and totalOrders from API response directly
          // DO NOT recalculate from chartData as it may not include all data points
          const brandRevenue = Math.round(salesData.totalRevenue || 0);  // Round to whole number
          const brandOrders = salesData.totalOrders || 0;
          
          totalRevenue += brandRevenue;
          totalOrders += brandOrders;
          
          console.log(`  Revenue: $${brandRevenue}, Growth Rate: ${salesData.growthRate}%`);
          
          // Aggregate growth rates (weighted average by revenue)
          if (salesData.growthRate !== undefined && brandRevenue > 0) {
            console.log(`    ✅ Adding revenue growth rate: ${salesData.growthRate}%`);
            revenueGrowthRateSum += salesData.growthRate;
            brandsWithRevenueData++;
          } else {
            console.log(`    ❌ Skipping revenue growth rate (growthRate=${salesData.growthRate}, revenue=${brandRevenue})`);
          }
          
          // Aggregate daily revenue data for charts
          salesData.data.forEach(item => {
            const date = item.date;
            if (!aggregatedRevenueData.has(date)) {
              aggregatedRevenueData.set(date, { date, revenue: 0, orders: 0 });
            }
            const existing = aggregatedRevenueData.get(date);
            existing.revenue += Math.round(item.revenue || 0);  // Round chart data too
            existing.orders += item.orders || 0;
          });
        }
        
        // Aggregate customer data
        if (customerData) {
          // Use totalCustomers directly - don't sum new+returning as they can overlap
          // (a customer can be both new AND returning in the same period)
          totalNewCustomers += customerData.newCustomers || 0;
          totalReturningCustomers += customerData.returningCustomers || 0;
          
          console.log(`  Customers: ${customerData.totalCustomers}, Growth Rate: ${customerData.growthRate}%`);
          
          // Aggregate customer growth rates
          if (customerData.growthRate !== undefined) {
            console.log(`    ✅ Adding customer growth rate: ${customerData.growthRate}%`);
            customerGrowthRateSum += customerData.growthRate;
            brandsWithCustomerData++;
          } else {
            console.log(`    ❌ Skipping customer growth rate (growthRate=${customerData.growthRate})`);
          }
        }
        
        // Aggregate session data for proper conversion rate calculation
        if (sessionData) {
          totalSessions += sessionData.totalSessions || 0;
          totalConversions += sessionData.totalConversions || 0;
          if (sessionData.percentageChange !== undefined && sessionData.totalSessions > 0) {
            conversionGrowthRateSum += sessionData.percentageChange;
            brandsWithConversionData++;
          }
        }
        
        // Aggregate product data
        if (productData?.topProducts) {
          productData.topProducts.forEach(product => {
            if (aggregatedProducts.has(product.id)) {
              const existing = aggregatedProducts.get(product.id);
              existing.revenue += sanitizeMetricValue(product.revenue || 0, 1000000000, `product.${product.id}.revenue`);
              existing.unitsSold += product.unitsSold || 0;
            } else {
              aggregatedProducts.set(product.id, {
                id: product.id,
                name: product.name,
                revenue: sanitizeMetricValue(product.revenue || 0, 1000000000, `product.${product.id}.revenue`),
                unitsSold: product.unitsSold || 0,
                category: product.category || 'General'
              });
            }
          });
        }
        
        // Aggregate marketing data
        if (marketingData?.channels) {
          marketingData.channels.forEach(channel => {
            if (!aggregatedMarketingChannels.has(channel.name)) {
              aggregatedMarketingChannels.set(channel.name, {
                name: channel.name,
                spend: 0,
                revenue: 0,
                impressions: 0,
                clicks: 0
              });
            }
            const existing = aggregatedMarketingChannels.get(channel.name);
            existing.spend += channel.spend || 0;
            existing.revenue += channel.revenue || 0;
            existing.impressions += channel.impressions || 0;
            existing.clicks += channel.clicks || 0;
          });
        }
      });
      
      // Calculate aggregated metrics
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      // NOTE: totalNewCustomers + totalReturningCustomers can double-count customers who
      // placed both new and returning orders in the same period. For now, we sum them
      // but this is a known limitation. Proper fix requires querying distinct customer_id
      // across all brands' orders_table at the company level.
      const totalCustomers = totalNewCustomers + totalReturningCustomers;
      // Use sessions for proper conversion rate calculation (sessions -> conversions)
      const conversionRate = totalSessions > 0 ? (totalConversions / totalSessions) * 100 : 0;
      
      // Calculate average growth rates across brands
      const avgRevenueGrowth = brandsWithRevenueData > 0 ? revenueGrowthRateSum / brandsWithRevenueData : 0;
      const avgCustomerGrowth = brandsWithCustomerData > 0 ? customerGrowthRateSum / brandsWithCustomerData : 0;
      
      console.log('=== DEBUG: Final Calculations ===');
      console.log(`Revenue Growth Sum: ${revenueGrowthRateSum}, Brands: ${brandsWithRevenueData}, Average: ${avgRevenueGrowth.toFixed(1)}%`);
      console.log(`Customer Growth Sum: ${customerGrowthRateSum}, Brands: ${brandsWithCustomerData}, Average: ${avgCustomerGrowth.toFixed(1)}%`);
      console.log(`Conversion Rate Calculation: ${totalConversions} conversions / ${totalSessions} sessions = ${conversionRate.toFixed(2)}%`);
      
      // Update state with aggregated data and real percentage changes
      const newMetricsData = {
        revenue: { 
          value: totalRevenue, 
          change: avgRevenueGrowth,
          trend: avgRevenueGrowth >= 0 ? 'up' : 'down' 
        },
        orders: { 
          value: totalOrders, 
          change: avgRevenueGrowth, // Use revenue growth as proxy for orders
          trend: avgRevenueGrowth >= 0 ? 'up' : 'down' 
        },
        customers: { 
          value: totalCustomers, 
          change: avgCustomerGrowth, 
          trend: avgCustomerGrowth >= 0 ? 'up' : 'down' 
        },
        conversion: { 
          value: conversionRate, 
          change: 0, // Conversion rate change would require separate calculation
          trend: 'stable' 
        }
      };
      
      console.log('=== DEBUG: Setting metricsData ===', newMetricsData);
      setMetricsData(newMetricsData);
      
      // Set chart data with validation
      setChartData({
        revenue: sanitizeChartData(Array.from(aggregatedRevenueData.values()).sort((a, b) => new Date(a.date) - new Date(b.date))),
        marketing: Array.from(aggregatedMarketingChannels.values()).map(ch => ({
          name: ch.name,
          value: sanitizeMetricValue(ch.revenue || 0, 1000000000, `marketing.${ch.name}.revenue`)
        })).filter(ch => ch.value > 0), // Remove zero-value marketing channels
        products: Array.from(aggregatedProducts.values())
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 10)
          .map(product => ({
            name: product.name,
            value: product.revenue,
            units: product.unitsSold,
            category: product.category
          })),
        activity: []
      });
      
      // Store all brand data for filtering
      const processedBrandData = allBrandData.map(({ brand, salesData, customerData, marketingData, sessionData, productData }) => ({
        ...brand,
        rawData: { salesData, customerData, marketingData, sessionData, productData },
        metrics: {
          // Use totals from API responses, not recalculated from chartData
          revenue: Math.round(salesData?.totalRevenue || 0),  // Round to whole number
          orders: salesData?.totalOrders || 0,
          // Use totalCustomers from API response instead of summing new+returning
          customers: customerData?.totalCustomers || 0,
          newCustomers: customerData?.newCustomers || 0,
          returningCustomers: customerData?.returningCustomers || 0,
          sessions: sessionData?.totalSessions || 0,
          conversions: sessionData?.totalConversions || 0,
          hasData: !!(salesData?.data?.length || customerData?.newCustomers || marketingData?.channels?.length || sessionData?.totalSessions || productData?.topProducts?.length)
        }
      }));
      
      setAllBrandData(processedBrandData);
      setBrandData(processedBrandData);
      
      // Platform breakdown will be fetched separately via useEffect
      // to ensure it updates when brand selection or timeRange changes
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPlatformBreakdown = async () => {
    try {
      if (selectedBrand === 'all' && brandData.length > 0) {
        // Fetch platform breakdown for ALL brands and aggregate
        const promises = brandData.map(brand =>
          dataService.getPlatformBreakdown(brand.id, timeRange).catch(() => null)
        );
        const results = await Promise.all(promises);

        // Aggregate platform data across all brands
        const platformMap = new Map();
        results.forEach(result => {
          if (result?.platforms) {
            result.platforms.forEach(platform => {
              const name = platform.name;
              if (!platformMap.has(name)) {
                platformMap.set(name, { name, revenue: 0, orders: 0, customers: 0 });
              }
              const existing = platformMap.get(name);
              existing.revenue += platform.revenue || 0;
              existing.orders += platform.orders || 0;
              existing.customers += platform.customers || 0;
            });
          }
        });

        const aggregatedPlatforms = Array.from(platformMap.values()).sort((a, b) => b.revenue - a.revenue);
        const totalRevenue = aggregatedPlatforms.reduce((sum, p) => sum + p.revenue, 0);
        const totalOrders = aggregatedPlatforms.reduce((sum, p) => sum + p.orders, 0);
        const totalCustomers = aggregatedPlatforms.reduce((sum, p) => sum + p.customers, 0);

        setPlatformData({
          platforms: aggregatedPlatforms.map(p => ({
            ...p,
            revenuePercentage: totalRevenue > 0 ? ((p.revenue / totalRevenue) * 100).toFixed(2) : 0,
            ordersPercentage: totalOrders > 0 ? ((p.orders / totalOrders) * 100).toFixed(2) : 0,
            customersPercentage: totalCustomers > 0 ? ((p.customers / totalCustomers) * 100).toFixed(2) : 0,
            avgOrderValue: p.orders > 0 ? (p.revenue / p.orders).toFixed(2) : 0
          })),
          totalRevenue,
          totalOrders,
          totalCustomers
        });
      } else if (selectedBrand !== 'all') {
        const platformBreakdown = await dataService.getPlatformBreakdown(selectedBrand, timeRange);
        if (platformBreakdown) {
          setPlatformData(platformBreakdown);
        } else {
          setPlatformData({ platforms: [], totalRevenue: 0, totalOrders: 0, totalCustomers: 0 });
        }
      }
    } catch (platformError) {
      console.error('Error fetching platform breakdown:', platformError);
      setPlatformData({ platforms: [], totalRevenue: 0, totalOrders: 0, totalCustomers: 0 });
    }
  };
  
  const updateDashboardForSelectedBrand = () => {
    if (selectedBrand === 'all') {
      // Show company-level aggregated data
      const totalRevenue = allBrandData.reduce((sum, brand) => sum + brand.metrics.revenue, 0);
      const totalOrders = allBrandData.reduce((sum, brand) => sum + brand.metrics.orders, 0);
      const totalCustomers = allBrandData.reduce((sum, brand) => sum + brand.metrics.customers, 0);
      const totalSessions = allBrandData.reduce((sum, brand) => sum + (brand.metrics.sessions || 0), 0);
      const totalConversions = allBrandData.reduce((sum, brand) => sum + (brand.metrics.conversions || 0), 0);
      
      // Calculate average growth rates from brand data (same logic as initial fetch)
      let revenueGrowthSum = 0;
      let customerGrowthSum = 0;
      let conversionGrowthSum = 0;
      let brandsWithRevenueGrowth = 0;
      let brandsWithCustomerGrowth = 0;
      let brandsWithConversionGrowth = 0;

      allBrandData.forEach(brand => {
        if (brand.rawData?.salesData?.growthRate !== undefined && brand.metrics.revenue > 0) {
          revenueGrowthSum += brand.rawData.salesData.growthRate;
          brandsWithRevenueGrowth++;
        }
        if (brand.rawData?.customerData?.growthRate !== undefined) {
          customerGrowthSum += brand.rawData.customerData.growthRate;
          brandsWithCustomerGrowth++;
        }
        if (brand.rawData?.sessionData?.percentageChange !== undefined && (brand.metrics.sessions || 0) > 0) {
          conversionGrowthSum += brand.rawData.sessionData.percentageChange;
          brandsWithConversionGrowth++;
        }
      });

      const avgRevenueGrowth = brandsWithRevenueGrowth > 0 ? revenueGrowthSum / brandsWithRevenueGrowth : 0;
      const avgCustomerGrowth = brandsWithCustomerGrowth > 0 ? customerGrowthSum / brandsWithCustomerGrowth : 0;
      const avgConversionGrowth = brandsWithConversionGrowth > 0 ? conversionGrowthSum / brandsWithConversionGrowth : 0;
      
      // Aggregate revenue chart data
      const aggregatedRevenueData = new Map();
      const aggregatedMarketingChannels = new Map();
      const aggregatedProducts = new Map();
      
      allBrandData.forEach(brand => {
        const { salesData, marketingData, productData } = brand.rawData;
        
        // Aggregate daily revenue data
        if (salesData?.data) {
          salesData.data.forEach(item => {
            const date = item.date;
            if (!aggregatedRevenueData.has(date)) {
              aggregatedRevenueData.set(date, { date, revenue: 0, orders: 0 });
            }
            const existing = aggregatedRevenueData.get(date);
            existing.revenue += sanitizeMetricValue(item.revenue || 0, 1000000000, `company.revenue.${date}`);
            existing.orders += item.orders || 0;
          });
        }
        
        // Aggregate marketing data
        if (marketingData?.channels) {
          marketingData.channels.forEach(channel => {
            if (!aggregatedMarketingChannels.has(channel.name)) {
              aggregatedMarketingChannels.set(channel.name, {
                name: channel.name,
                spend: 0,
                revenue: 0,
                impressions: 0,
                clicks: 0
              });
            }
            const existing = aggregatedMarketingChannels.get(channel.name);
            existing.spend += sanitizeMetricValue(channel.spend || 0, 100000000, `company.marketing.${channel.name}.spend`);
            existing.revenue += sanitizeMetricValue(channel.revenue || 0, 1000000000, `company.marketing.${channel.name}.revenue`);
            existing.impressions += channel.impressions || 0;
            existing.clicks += channel.clicks || 0;
          });
        }
        
        // Aggregate product data
        if (productData?.topProducts) {
          productData.topProducts.forEach(product => {
            if (aggregatedProducts.has(product.id)) {
              const existing = aggregatedProducts.get(product.id);
              existing.revenue += sanitizeMetricValue(product.revenue || 0, 1000000000, `company.product.${product.id}.revenue`);
              existing.unitsSold += product.unitsSold || 0;
            } else {
              aggregatedProducts.set(product.id, {
                id: product.id,
                name: product.name,
                revenue: sanitizeMetricValue(product.revenue || 0, 1000000000, `company.product.${product.id}.revenue`),
                unitsSold: product.unitsSold || 0,
                category: product.category || 'General'
              });
            }
          });
        }
      });
      
      // Conversion rate = orders / sessions
      const conversionRate = totalSessions > 0 ? (totalConversions / totalSessions) * 100 : 0;

      setMetricsData({
        revenue: {
          value: totalRevenue,
          change: avgRevenueGrowth,
          trend: avgRevenueGrowth >= 0 ? 'up' : 'down'
        },
        orders: {
          value: totalOrders,
          change: avgRevenueGrowth, // Use revenue growth as proxy
          trend: avgRevenueGrowth >= 0 ? 'up' : 'down'
        },
        customers: {
          value: totalCustomers,
          change: avgCustomerGrowth,
          trend: avgCustomerGrowth >= 0 ? 'up' : 'down'
        },
        conversion: {
          value: parseFloat(conversionRate.toFixed(2)),
          change: avgConversionGrowth,
          trend: avgConversionGrowth >= 0 ? 'up' : 'down'
        }
      });
      
      const revenueData = sanitizeChartData(Array.from(aggregatedRevenueData.values()).sort((a, b) => new Date(a.date) - new Date(b.date)));
      console.log('[DASHBOARD] Revenue chart data sample:', revenueData.slice(0, 3));
      
      setChartData({
        revenue: revenueData,
        marketing: Array.from(aggregatedMarketingChannels.values()).map(ch => ({
          name: ch.name,
          value: ch.revenue || ch.spend || 0
        })).filter(ch => ch.value > 0),
        products: Array.from(aggregatedProducts.values())
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 10)
          .map(product => ({
            name: product.name,
            value: product.revenue,
            units: product.unitsSold,
            category: product.category
          })),
        activity: []
      });
      
    } else {
      // Show specific brand data
      const brand = allBrandData.find(b => b.id.toString() === selectedBrand.toString());
      if (brand) {
        const { salesData, customerData, marketingData, sessionData, productData } = brand.rawData;
        
        const brandRevenue = brand.metrics.revenue;
        const brandOrders = brand.metrics.orders;
        const brandCustomers = brand.metrics.customers;
        
        // Get growth rates from raw data
        const revenueGrowth = salesData?.growthRate || 0;
        const customerGrowth = customerData?.growthRate || 0;
        
        // Use session data for conversion rate if available
        // Conversion rate = (transactions / sessions) * 100
        // NOTE: We do NOT use orders/customers as a fallback because:
        // 1. It's not a conversion rate (customers can place multiple orders)
        // 2. It can exceed 100% which is confusing
        // 3. Without session data, we simply don't have a conversion rate
        let conversionRate = 0;
        if (sessionData?.totalSessions > 0 && sessionData?.totalConversions > 0) {
          conversionRate = (sessionData.totalConversions / sessionData.totalSessions) * 100;
        }
        
        setMetricsData({
          revenue: { 
            value: brandRevenue, 
            change: revenueGrowth,
            trend: revenueGrowth >= 0 ? 'up' : 'down'
          },
          orders: { 
            value: brandOrders, 
            change: revenueGrowth, // Use revenue growth as proxy
            trend: revenueGrowth >= 0 ? 'up' : 'down'
          },
          customers: { 
            value: brandCustomers, 
            change: customerGrowth,
            trend: customerGrowth >= 0 ? 'up' : 'down'
          },
          conversion: {
            value: parseFloat(conversionRate.toFixed(2)),
            change: sessionData?.percentageChange || 0,
            trend: (sessionData?.percentageChange || 0) >= 0 ? 'up' : 'down'
          }
        });

        // Set brand-specific chart data
        const brandProducts = productData?.topProducts ? productData.topProducts.map(product => ({
          name: product.name,
          value: sanitizeMetricValue(product.revenue || 0, 1000000000, `brand.${brand.id}.product.revenue`),
          units: product.unitsSold || 0,
          category: product.category || 'General'
        })) : [];
        
        setChartData({
          revenue: salesData?.data ? sanitizeChartData(salesData.data.sort((a, b) => new Date(a.date) - new Date(b.date))) : [],
          marketing: marketingData?.channels ? marketingData.channels.map(ch => ({
            name: ch.name,
            value: sanitizeMetricValue(ch.revenue || ch.spend || 0, 1000000000, `brand.${brand.id}.marketing.${ch.name}`)
          })).filter(ch => ch.value > 0) : [],
          products: brandProducts,
          activity: []
        });
      }
    }
  };
  
  const fetchConnectors = async () => {
    try {
      const companyId = user?.companyId || 1;
      
      // NEW: If viewing all brands, aggregate connectors from ALL brands
      if (selectedBrand === 'all' && brandData.length > 0) {
        console.log(`Fetching connectors for all ${brandData.length} brands`);
        
        // Fetch connectors for each brand in parallel
        const allConnectorPromises = brandData.map(async (brand) => {
          try {
            const connectorStatus = await dataService.getDataSourcesStatus(brand.id);
            return connectorStatus || [];
          } catch (err) {
            console.error(`Error fetching connectors for brand ${brand.id}:`, err);
            return [];
          }
        });
        
        const allConnectorResults = await Promise.all(allConnectorPromises);
        const flatConnectors = allConnectorResults.flat();
        
        if (flatConnectors.length > 0) {
          console.log(`Found ${flatConnectors.length} total connectors across all brands`);
          
          // Group connectors by type
          const groupedConnectors = flatConnectors.reduce((groups, connector) => {
            const type = connector.type || 'source';
            const name = connector.name || 'Unknown';
            const platformName = (type || name || '').toLowerCase();
            
            let standardizedType = type;
            if (platformName.includes('facebook') || platformName.includes('meta')) {
              standardizedType = 'facebook';
            } else if (platformName.includes('google_analytics') || platformName.includes('ga4') || platformName.includes('analytics')) {
              standardizedType = 'google_analytics';
            } else if (platformName.includes('google_ads') || platformName.includes('google ads')) {
              standardizedType = 'google_ads';
            } else if (platformName.includes('shopify')) {
              standardizedType = 'shopify';
            } else if (platformName.includes('klaviyo')) {
              standardizedType = 'klaviyo';
            } else if (platformName.includes('amazon')) {
              standardizedType = 'amazon';
            } else if (platformName.includes('tiktok')) {
              standardizedType = 'tiktok';
            } else if (platformName.includes('slack')) {
              standardizedType = 'slack';
            } else if (platformName.includes('pinterest')) {
              standardizedType = 'pinterest';
            }
            
            if (!groups[standardizedType]) {
              groups[standardizedType] = {
                type: standardizedType,
                name: getConnectorDisplayName(standardizedType, name),
                count: 0,
                status: 'active',
                lastSync: connector.lastSync || 'Recently',
                connectors: []
              };
            }
            
            groups[standardizedType].count += 1;
            groups[standardizedType].connectors.push(connector);
            
            return groups;
          }, {});
          
          setConnectors(Object.values(groupedConnectors));
          return;
        }
      }
      
      // Single brand view - get current brand context
      const currentBrandId = selectedBrand === 'all' ? 
        (brandData.length > 0 ? brandData[0].id : null) : selectedBrand;
      
      if (!currentBrandId) {
        console.log('No brand selected for connector status');
        setConnectors([]);
        return;
      }
      
      console.log(`Fetching connectors for brand ${currentBrandId}`);
      
      // Use the analytics API to get connector status based on available data
      const connectorStatus = await dataService.getDataSourcesStatus(currentBrandId);
      
      if (connectorStatus && connectorStatus.length > 0) {
        console.log(`Found ${connectorStatus.length} platform groups:`, connectorStatus.map(c => `${c.name}: ${c.count} connections`));
        
        // Backend now returns pre-grouped connectors by platform with counts
        // No need to re-group here
        setConnectors(connectorStatus);
      } else {
        console.log('No connectors found, checking alternative sources...');
        
        // Fallback: try the old connector service as backup
        const [connections, sources] = await Promise.allSettled([
          connectorService.getConnections(),
          connectorService.getSources()
        ]);
        
        let allConnectors = [];
        
        if (connections.status === 'fulfilled' && connections.value) {
          allConnectors = [...allConnectors, ...connections.value];
        }
        
        if (sources.status === 'fulfilled' && sources.value) {
          allConnectors = [...allConnectors, ...sources.value];
        }
        
        console.log(`Fallback connectors found: ${allConnectors.length}`);
        setConnectors(allConnectors);
      }
      
    } catch (err) {
      console.error('Error fetching connectors:', err);
      // Set some default connectors so the UI doesn't look empty
      setConnectors([
        {
          name: 'Data Sources',
          type: 'mixed',
          status: 'active',
          lastSync: 'Recently',
          count: 1
        }
      ]);
    }
  };
  
  const handleTimeRangeOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleTimeRangeClose = () => {
    setAnchorEl(null);
  };
  
  const handleTimeRangeChange = (range) => {
    if (range === 'custom') {
      // Open custom date dialog
      setCustomDateDialogOpen(true);
      handleTimeRangeClose();
    } else {
      setTimeRange(range);
      setCustomDateLabel(''); // Clear custom label when selecting preset ranges
      handleTimeRangeClose();
    }
  };
  
  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      // Validate dates
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      
      if (start > end) {
        alert('Start date cannot be after end date');
        return;
      }
      
      // Format the date range for backend (YYYY-MM-DD|YYYY-MM-DD)
      const rangeValue = `${customStartDate}|${customEndDate}`;
      setTimeRange(rangeValue);
      
      // Format label for display
      const startFormatted = new Date(customStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endFormatted = new Date(customEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      setCustomDateLabel(`${startFormatted} - ${endFormatted}`);
      
      setCustomDateDialogOpen(false);
    }
  };
  
  const handleCustomDateCancel = () => {
    setCustomDateDialogOpen(false);
    setCustomStartDate('');
    setCustomEndDate('');
  };
  
  const handleBrandFilterOpen = (event) => {
    setBrandFilterAnchorEl(event.currentTarget);
  };
  
  const handleBrandFilterClose = () => {
    setBrandFilterAnchorEl(null);
  };
  
  const handleBrandFilterChange = (brandId) => {
    setSelectedBrand(brandId);
    handleBrandFilterClose();
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const isMenuOpen = Boolean(anchorEl);
  const isBrandMenuOpen = Boolean(brandFilterAnchorEl);
  const menuId = 'time-range-menu';
  const brandMenuId = 'brand-filter-menu';
  
  const getSelectedBrandName = () => {
    if (selectedBrand === 'all') return 'All Brands';
    const brand = brandData.find(b => b.id.toString() === selectedBrand.toString());
    return brand ? brand.name : 'Unknown Brand';
  };

  // Show onboarding screen when company has no brands
  if (!hasBrands && !loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 3
      }}>
        <Card sx={{
          maxWidth: 600,
          width: '100%',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          overflow: 'visible'
        }}>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 3
            }}>
              <BarChartIcon sx={{ fontSize: 32, color: '#fff' }} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Welcome to SuperChargeMe
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Get started by creating your first brand and connecting your data sources.
            </Typography>

            <Box sx={{ textAlign: 'left', mb: 4 }}>
              {[
                { step: '1', title: 'Create your first brand', desc: 'Set up a brand to organize your data', path: '/brands' },
                { step: '2', title: 'Connect your data sources', desc: 'Link Shopify, Meta, Google Ads and more', path: '/connectors' },
                { step: '3', title: 'View your dashboard', desc: 'Track revenue, orders, and marketing performance', path: null },
              ].map((item) => (
                <Box
                  key={item.step}
                  onClick={item.path ? () => navigate(item.path) : undefined}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1.5,
                    borderRadius: 2, border: '1px solid', borderColor: 'divider',
                    cursor: item.path ? 'pointer' : 'default',
                    opacity: item.path ? 1 : 0.5,
                    transition: 'all 0.2s',
                    '&:hover': item.path ? {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    } : {}
                  }}
                >
                  <Avatar sx={{
                    width: 36, height: 36,
                    bgcolor: item.path ? 'primary.main' : 'action.disabled',
                    fontSize: 14, fontWeight: 700
                  }}>
                    {item.step}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{item.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={<AddCircleOutline />}
              onClick={() => navigate('/brands')}
              sx={{
                borderRadius: 2, px: 4,
                background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
                boxShadow: '0 4px 12px var(--color-primary-a30)',
              }}
            >
              Create Your First Brand
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, letterSpacing: '-0.025em', fontSize: { xs: '1.75rem', md: '2rem' } }}>
                Dashboard {selectedBrand !== 'all' && `- ${getSelectedBrandName()}`}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Welcome back, {user?.fullName || 'User'}! Here's what's happening with your {selectedBrand === 'all' ? 'company' : 'brand'} data.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchDashboardData}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => setExportOpen(true)}
                sx={{ borderRadius: 2 }}
              >
                Export
              </Button>
              {brandData.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<People />}
                  endIcon={<KeyboardArrowDown />}
                  onClick={handleBrandFilterOpen}
                  aria-controls={brandMenuId}
                  aria-haspopup="true"
                  aria-expanded={isBrandMenuOpen ? 'true' : undefined}
                  sx={{ 
                    borderRadius: 2,
                    minWidth: 140
                  }}
                >
                  {getSelectedBrandName()}
                </Button>
              )}
              <Button
                color="primary"
                variant="contained"
                startIcon={<CalendarToday />}
                endIcon={<KeyboardArrowDown />}
                onClick={handleTimeRangeOpen}
                aria-controls={menuId}
                aria-haspopup="true"
                aria-expanded={isMenuOpen ? 'true' : undefined}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
                }}
              >
                {customDateLabel ? customDateLabel :
                 timeRange === '7days' ? 'Last 7 Days' :
                 timeRange === '30days' ? 'Last 30 Days' :
                 timeRange === 'month' ? 'This Month' :
                 timeRange === 'lastmonth' ? 'Last Month' :
                 timeRange === 'year' ? 'This Year' :
                 timeRange === 'lastyear' ? 'Last Year' :
                 timeRange.includes('|') ? 'Custom Range' : 'Last 30 Days'}
              </Button>
              <Menu
                anchorEl={anchorEl}
                id={menuId}
                keepMounted
                open={isMenuOpen}
                onClose={handleTimeRangeClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    borderRadius: 2,
                    minWidth: 180
                  }
                }}
              >
                <MenuItem onClick={() => handleTimeRangeChange('7days')}>Last 7 Days</MenuItem>
                <MenuItem onClick={() => handleTimeRangeChange('30days')}>Last 30 Days</MenuItem>
                <MenuItem onClick={() => handleTimeRangeChange('month')}>This Month</MenuItem>
                <MenuItem onClick={() => handleTimeRangeChange('lastmonth')}>Last Month</MenuItem>
                <MenuItem onClick={() => handleTimeRangeChange('year')}>This Year</MenuItem>
                <MenuItem onClick={() => handleTimeRangeChange('lastyear')}>Last Year</MenuItem>
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={() => handleTimeRangeChange('custom')}>Custom Range...</MenuItem>
              </Menu>
              
              {/* Custom Date Range Dialog */}
              <Dialog 
                open={customDateDialogOpen} 
                onClose={handleCustomDateCancel}
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    minWidth: 400
                  }
                }}
              >
                <DialogTitle>Select Custom Date Range</DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCustomDateCancel}>Cancel</Button>
                  <Button 
                    onClick={handleCustomDateApply} 
                    variant="contained"
                    disabled={!customStartDate || !customEndDate}
                  >
                    Apply
                  </Button>
                </DialogActions>
              </Dialog>
              
              {/* Brand Filter Menu */}
              <Menu
                anchorEl={brandFilterAnchorEl}
                id={brandMenuId}
                keepMounted
                open={isBrandMenuOpen}
                onClose={handleBrandFilterClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    borderRadius: 2,
                    minWidth: 200,
                    maxHeight: 400
                  }
                }}
              >
                <MenuItem 
                  onClick={() => handleBrandFilterChange('all')}
                  selected={selectedBrand === 'all'}
                  sx={{
                    fontWeight: selectedBrand === 'all' ? 600 : 400,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                      All
                    </Avatar>
                    All Brands (Company View)
                  </Box>
                </MenuItem>
                {brandData.map((brand) => (
                  <MenuItem 
                    key={brand.id}
                    onClick={() => handleBrandFilterChange(brand.id.toString())}
                    selected={selectedBrand === brand.id.toString()}
                    sx={{
                      fontWeight: selectedBrand === brand.id.toString() ? 600 : 400
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar 
                        sx={{ 
                          width: 24, 
                          height: 24, 
                          bgcolor: brand.metrics.hasData ? 'success.light' : 'grey.300',
                          color: brand.metrics.hasData ? 'success.dark' : 'grey.600',
                          fontSize: '0.75rem'
                        }}
                      >
                        {brand.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{brand.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {brand.metrics.hasData ? `$${brand.metrics.revenue.toLocaleString()}` : 'No data'}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
          </Grid>
        )}

        {selectedBrand === 'all' && incompleteBrands.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Company totals are partial: couldn't load data for {incompleteBrands.length} brand{incompleteBrands.length > 1 ? 's' : ''} ({incompleteBrands.join(', ')}). Try refreshing.
            </Alert>
          </Grid>
        )}

        {/* Key Metrics */}
        <Grid item xs={12} md={6} lg={3}>
          <Card 
            sx={{ 
              height: '100%'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ width: '100%' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2rem' }, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                    {loading ? <CircularProgress size={20} /> : `$${metricsData.revenue.value.toLocaleString()}`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {metricsData.revenue.trend === 'up' ? (
                      <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : metricsData.revenue.trend === 'down' ? (
                      <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                    ) : null}
                    <Typography variant="body2" color={metricsData.revenue.trend === 'up' ? 'success.main' : metricsData.revenue.trend === 'down' ? 'error.main' : 'text.secondary'}>
                      {metricsData.revenue.change !== 0 ? `${metricsData.revenue.change > 0 ? '+' : ''}${metricsData.revenue.change.toFixed(1)}%` : 'No change'}
                    </Typography>
                  </Box>
                  {/* Platform Breakdown */}
                  {platformData.platforms && platformData.platforms.length > 0 && (
                    <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        By Platform
                      </Typography>
                      {platformData.platforms.map((platform, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLORS[index % COLORS.length] }} />
                              <Typography variant="caption">{platform.name}</Typography>
                            </Box>
                            <Typography variant="caption" fontWeight={600}>
                              ${(platform.revenue / 1000).toFixed(0)}k
                            </Typography>
                          </Box>
                          <Box sx={{ height: 4, bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden' }}>
                            <Box 
                              sx={{ 
                                height: '100%', 
                                width: `${platform.revenuePercentage}%`, 
                                bgcolor: COLORS[index % COLORS.length],
                                transition: 'width 0.3s ease'
                              }} 
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'var(--color-primary-a10)',
                    color: 'primary.main',
                    height: 48,
                    width: 48
                  }}
                >
                  <AttachMoney />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Card 
            sx={{ 
              height: '100%'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ width: '100%' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
                    Total Orders
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2rem' }, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                    {loading ? <CircularProgress size={20} /> : metricsData.orders.value.toLocaleString()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {metricsData.orders.trend === 'up' ? (
                      <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : metricsData.orders.trend === 'down' ? (
                      <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                    ) : null}
                    <Typography variant="body2" color={metricsData.orders.trend === 'up' ? 'success.main' : metricsData.orders.trend === 'down' ? 'error.main' : 'text.secondary'}>
                      {metricsData.orders.change !== 0 ? `${metricsData.orders.change > 0 ? '+' : ''}${metricsData.orders.change.toFixed(1)}%` : 'No change'}
                    </Typography>
                  </Box>
                  {/* Platform Breakdown */}
                  {platformData.platforms && platformData.platforms.length > 0 && (
                    <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        By Platform
                      </Typography>
                      {platformData.platforms.map((platform, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLORS[index % COLORS.length] }} />
                              <Typography variant="caption">{platform.name}</Typography>
                            </Box>
                            <Typography variant="caption" fontWeight={600}>
                              {platform.orders.toLocaleString()}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 4, bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden' }}>
                            <Box 
                              sx={{ 
                                height: '100%', 
                                width: `${platform.ordersPercentage}%`, 
                                bgcolor: COLORS[index % COLORS.length],
                                transition: 'width 0.3s ease'
                              }} 
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'var(--color-secondary-a10)',
                    color: 'secondary.main',
                    height: 48,
                    width: 48
                  }}
                >
                  <ShoppingCart />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Card 
            sx={{ 
              height: '100%'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ width: '100%' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
                    Total Customers
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2rem' }, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                    {loading ? <CircularProgress size={20} /> : metricsData.customers.value.toLocaleString()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {metricsData.customers.trend === 'up' ? (
                      <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : metricsData.customers.trend === 'down' ? (
                      <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                    ) : null}
                    <Typography variant="body2" color={metricsData.customers.trend === 'up' ? 'success.main' : metricsData.customers.trend === 'down' ? 'error.main' : 'text.secondary'}>
                      {metricsData.customers.change !== 0 ? `${metricsData.customers.change > 0 ? '+' : ''}${metricsData.customers.change.toFixed(1)}%` : 'No change'}
                    </Typography>
                  </Box>
                  {/* Platform Breakdown */}
                  {platformData.platforms && platformData.platforms.length > 0 && (
                    <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        By Platform
                      </Typography>
                      {platformData.platforms.map((platform, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLORS[index % COLORS.length] }} />
                              <Typography variant="caption">{platform.name}</Typography>
                            </Box>
                            <Typography variant="caption" fontWeight={600}>
                              {platform.customers.toLocaleString()}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 4, bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden' }}>
                            <Box 
                              sx={{ 
                                height: '100%', 
                                width: `${platform.customersPercentage}%`, 
                                bgcolor: COLORS[index % COLORS.length],
                                transition: 'width 0.3s ease'
                              }} 
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(123, 181, 92, 0.1)',
                    color: 'success.main',
                    height: 48,
                    width: 48
                  }}
                >
                  <People />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Card 
            sx={{ 
              height: '100%'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
                    Conversion Rate
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2rem' }, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                    {loading ? <CircularProgress size={20} /> : `${metricsData.conversion.value.toFixed(1)}%`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {metricsData.conversion.trend === 'up' ? (
                      <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : metricsData.conversion.trend === 'down' ? (
                      <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                    ) : null}
                    <Typography variant="body2" color={metricsData.conversion.trend === 'up' ? 'success.main' : metricsData.conversion.trend === 'down' ? 'error.main' : 'text.secondary'}>
                      {metricsData.conversion.change !== 0 ? `${metricsData.conversion.change > 0 ? '+' : ''}${metricsData.conversion.change.toFixed(1)}%` : 'No change'}
                    </Typography>
                  </Box>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255, 82, 82, 0.1)',
                    color: 'error.main',
                    height: 48,
                    width: 48
                  }}
                >
                  <TrendingDown />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Chart */}
        <Grid item xs={12} lg={8}>
          <Card
          >
            <CardHeader
              title="Revenue Overview"
              action={
                <IconButton aria-label="settings" disabled>
                  <MoreVert />
                </IconButton>
              }
              sx={{
                '& .MuiCardHeader-title': {
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif'
                }
              }}
            />
            <Divider />
            <CardContent>
              {chartData.revenue.length > 0 ? (
                <ResponsiveContainer width="100%" height={380}>
                  <LineChart data={chartData.revenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-faint)" />
                    <XAxis
                      dataKey="date"
                      stroke="var(--text-muted)"
                      fontSize={12}
                      interval={chartData.revenue.length <= 14 ? 0 : Math.floor(chartData.revenue.length / 10) - 1}
                      tickFormatter={(value) => {
                        // Parse directly from string to avoid Date() timezone bugs
                        if (typeof value === 'string') {
                          // Handle "YYYY-MM-DD" format
                          const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                          if (isoMatch) {
                            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                            // Year view: show month only
                            if (chartData.revenue.length > 92) return months[parseInt(isoMatch[2]) - 1];
                            return `${months[parseInt(isoMatch[2]) - 1]} ${parseInt(isoMatch[3])}`;
                          }
                          // Handle "Mar 29 '26" format
                          // Year view: show month only
                          if (chartData.revenue.length > 92) return value.replace(/ \d{1,2} '\d{2}$/, '');
                          // Otherwise strip year suffix
                          return value.replace(/ '\d{2}$/, '');
                        }
                        return value;
                      }}
                    />
                    <YAxis 
                      stroke="var(--text-muted)"
                      fontSize={12}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                      labelFormatter={(label) => {
                        // Return the label as-is — already formatted by backend
                        return label;
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="var(--color-primary)" 
                      strokeWidth={3}
                      dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: 'var(--color-primary)', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ 
                  height: 380, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'grey.50',
                  borderRadius: 2
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <BarChartIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No revenue data available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Connect your data sources to see revenue trends
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Marketing Chart */}
        <Grid item xs={12} lg={4}>
          <Card
          >
            <CardHeader
              title="Marketing Channels"
              action={
                <IconButton aria-label="settings" disabled>
                  <MoreVert />
                </IconButton>
              }
              sx={{
                '& .MuiCardHeader-title': {
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif'
                }
              }}
            />
            <Divider />
            <CardContent>
              {chartData.marketing.length > 0 ? (
                <ResponsiveContainer width="100%" height={380}>
                  <PieChart>
                    <Pie
                      data={chartData.marketing}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={140}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {chartData.marketing.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value.toLocaleString()}`, 'Value']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ 
                  height: 380, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'grey.50',
                  borderRadius: 2
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <BarChartIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No marketing data available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Connect your marketing platforms to see channel performance
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Connectors Status */}
        <Grid item xs={12}>
          <Card
          >
            <CardHeader
              title="Data Connectors"
              action={
                <Button
                  startIcon={<AddCircleOutline />}
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={() => window.location.href = '/connectors'}
                  sx={{ 
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
                  }}
                >
                  Add Connector
                </Button>
              }
              sx={{
                '& .MuiCardHeader-title': {
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif'
                }
              }}
            />
            <Divider />
            <CardContent>
              {connectors.length > 0 ? (
                <Grid container spacing={2}>
                  {connectors.slice(0, 6).map((connector, index) => {
                    // Handle different connector data structures
                    const connectorName = connector.name || connector.sourceName || `Connector ${index + 1}`;
                    const connectorStatus = connector.status === 'active' || connector.status === 'Active' ? 'active' : 'warning';
                    const connectorType = connector.platformName || connector.type || 'source';
                    const connectorCount = connector.count || 1;
                    const colors = getConnectorColor(connectorType, connectorName);
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} key={`connector-${index}`}>
                        <Card 
                          sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            border: '1px solid',
                            borderColor: colors.light,
                            background: `linear-gradient(135deg, ${colors.light} 0%, rgba(255,255,255,0.9) 100%)`,
                            '&:hover': {
                              borderColor: colors.main,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 16px ${colors.light}`,
                              transition: 'all 0.2s ease'
                            }
                          }}
                        >
                          {/* Connector Icon */}
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '12px',
                              bgcolor: colors.main,
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mx: 'auto',
                              mb: 2,
                              boxShadow: `0 4px 12px ${colors.light}`
                            }}
                          >
                            {getConnectorIcon(connectorType, connectorName)}
                          </Box>
                          
                          {/* Connector Name */}
                          <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: colors.dark }}>
                            {connectorName}
                          </Typography>
                          
                          {/* Connector Count */}
                          <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
                            {connectorCount} {connectorCount === 1 ? 'connection' : 'connections'}
                          </Typography>
                          
                          {/* Status Info */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              {connectorType} • {connector.lastSync || 'Recently'}
                            </Typography>
                          </Box>
                          
                          {/* Status Badge */}
                          <Chip
                            label={connectorStatus === 'active' ? 'Active' : 'Warning'}
                            size="small"
                            sx={{
                              bgcolor: connectorStatus === 'active' ? colors.main : '#ff9800',
                              color: 'white',
                              fontWeight: 600,
                              '& .MuiChip-label': {
                                px: 2
                              }
                            }}
                          />
                        </Card>
                      </Grid>
                    );
                  })}
                  {connectors.length > 6 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        and {connectors.length - 6} more connectors...
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <AddCircleOutline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No connectors configured
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Add data connectors to start seeing your dashboard populate with real data
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutline />}
                    onClick={() => window.location.href = '/connectors'}
                    sx={{ 
                      borderRadius: 2,
                      background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
                    }}
                  >
                    Add Your First Connector
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Product Categories & Customer Segments */}
        <Grid item xs={12} md={6}>
          <Card
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 0,
                    px: 3,
                    py: 2
                  }
                }}
              >
                <Tab label="Product Categories" />
                <Tab label="Customer Segments" />
              </Tabs>
            </Box>
            <CardContent>
              {tabValue === 0 ? (
                // Product Categories Tab
                chartData.products.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData.products}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-faint)" />
                      <XAxis 
                        dataKey="name" 
                        stroke="var(--text-muted)"
                        fontSize={12}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis 
                        stroke="var(--text-muted)"
                        fontSize={12}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        formatter={(value, name) => [
                          `${value.toLocaleString()}`, 
                          name === 'value' ? 'Revenue' : name
                        ]}
                        labelFormatter={(label) => `Product: ${label}`}
                      />
                      <Bar 
                        dataKey="value" 
                        fill="var(--color-primary)" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ 
                    height: 300, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                    borderRadius: 2
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <BarChartIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No product data available
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Product data will appear here once your data sources are connected
                      </Typography>
                    </Box>
                  </Box>
                )
              ) : (
                // Customer Segments Tab
                chartData.marketing.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData.marketing}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {chartData.marketing.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value.toLocaleString()}`, 'Revenue']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ 
                    height: 300, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                    borderRadius: 2
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <BarChartIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No customer segment data available
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Customer segments will appear here once your data sources are connected
                      </Typography>
                    </Box>
                  </Box>
                )
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Brand Performance Overview - Only show in company view */}
        {selectedBrand === 'all' && (
          <Grid item xs={12}>
            <Card
            >
              <CardHeader
                title="Brand Performance Overview"
                subheader={`${brandData.length} brands • ${brandData.filter(b => b.metrics.hasData).length} with data`}
                action={
                  <Button
                    startIcon={<AddCircleOutline />}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: 2 }}
                  >
                    Add Brand
                  </Button>
                }
                sx={{
                  '& .MuiCardHeader-title': {
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif'
                  }
                }}
              />
              <Divider />
              <CardContent>
                {brandData.length > 0 ? (
                  <Grid container spacing={2}>
                    {brandData.map((brand) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={brand.id}>
                        <Card 
                          className="connector-card"
                          sx={{ 
                            borderRadius: 2,
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '1px solid',
                            borderColor: brand.id.toString() === selectedBrand ? 'primary.main' : 
                                        brand.metrics.hasData ? 'success.light' : 'divider',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                              borderColor: 'primary.main'
                            }
                          }}
                          onClick={() => handleBrandFilterChange(brand.id.toString())}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  mr: 2,
                                  bgcolor: brand.metrics.hasData ? 'success.light' : 'grey.300',
                                  color: brand.metrics.hasData ? 'success.dark' : 'grey.600',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {brand.name.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={brand.name}
                                >
                                  {brand.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Brand ID: {brand.id}
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Revenue
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  ${brand.metrics.revenue.toLocaleString()}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Orders
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {brand.metrics.orders.toLocaleString()}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Customers
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {brand.metrics.customers.toLocaleString()}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pl: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  New
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 500, color: 'success.main' }}>
                                  {brand.metrics.newCustomers?.toLocaleString() || 0}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pl: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  Repeat
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 500, color: 'info.main' }}>
                                  {brand.metrics.returningCustomers?.toLocaleString() || 0}
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                              <Chip
                                label={brand.metrics.hasData ? 'Active' : 'No Data'}
                                size="small"
                                color={brand.metrics.hasData ? 'success' : 'default'}
                                sx={{ fontSize: '0.75rem' }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <AddCircleOutline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No brands configured
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Create your first brand to start tracking performance across multiple business units
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddCircleOutline />}
                      onClick={() => navigate('/brands')}
                      sx={{
                        borderRadius: 2,
                        background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
                      }}
                    >
                      Create Brand
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <GoogleSheetsExport open={exportOpen} onClose={() => setExportOpen(false)} />
    </Box>
  );
};

export default Dashboard;
